import React, { useState, useMemo, useEffect } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import { Edit02Icon, ViewIcon, DeleteIcon } from "@hugeicons/core-free-icons";
import {
  useGetMakkahGroupsQuery,
  useDeleteMakkahGroupMutation,
} from "@services/makkahGroups";
import { useDebouncedSearch } from "@utils/hooks/useDebouncedSearch";
import { useTranslation } from "react-i18next";
import { TableC } from "@/CommonElements/TableC";
import { useBasicDataQuery } from "@/services/basicData/basicData";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { DisplayDetailsModal } from "./DisplayDetailsModal";
import ConfirmModal from "@/CommonElements/ConfirmModal";
import { Trans } from "react-i18next";
import { SelectCountry } from "@Component/Form/SelectCountry/SelectCountry";
import { useMissionCompaniesQuery } from "@/services/basicData/basicData";
import { AddSquareIcon, PieChartIcon } from "@hugeicons/core-free-icons/index";
import { FileExcelIcon } from "@/assets/icons";
import { Dropdown } from "antd";
import BtnAsLink from "@/Component/BtnAsLink";
import { ImportCampaignModal } from "@/Pages/Campaigns/ImportCampaignModal";
import { useSystem } from "@/utils/hooks/useLocation";
import { usePermissions } from "@/utils/hooks/usePermissions";
import { permissions } from "@/utils/constants";
import { CountModal } from "@/Component/CountModal";
import { PilgrimGroupNameMappingInfiniteSelect } from "@/Component/InfiniteSearchSelect/PilgrimGroupNameMappingInfiniteSelect";

/** **Upcoming** tab only. MANAGE_SEGMENTS → `view_groups` (Makkah) / `view_groups_for_madina` (Madina). */
export const UpcommingGroups = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [showImportModal, setShowImportModal] = useState(false);
  const { from_id, currentSystem } = useSystem();
  const {
    hasAnyPermission
  } = usePermissions();
  const [filters, setFilters] = useState({
    direction_id: from_id,
  });
  const isMadina = currentSystem === "madina";
  /** VIEW + group number link. Madina: `view_groups_for_madina` (main group). */
  const canViewGroup = hasAnyPermission([
    isMadina ? permissions.view_groups_for_madina : permissions.view_groups
  ]);
  /** edit / EDIT_GROUP: Madina → update_groups_for_madina */
  const canUpdateMainGroup = hasAnyPermission([
    isMadina ? permissions.update_groups_for_madina : permissions.update_groups
  ]);
  /** MANAGE_SEGMENTS: `view_groups` / `view_groups_for_madina` */
  const canAccessSegments = hasAnyPermission([
    isMadina ? permissions.view_groups_for_madina : permissions.view_groups
  ]);
  /** delete: Madina → delete_groups_for_madina */
  const canDeleteGroup = hasAnyPermission([
    isMadina ? permissions.delete_groups_for_madina : permissions.delete_groups
  ]);
  const canCreateGroup = hasAnyPermission([
    isMadina ? permissions.create_groups_for_madina : permissions.create_groups
  ]);
  const [detailsModalState, setDetailsModalState] = useState({
    isOpen: false,
    details: [],
  });
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    groupId: null,
  });

  // Fetch basic data
  const { data: basicDataRes } = useBasicDataQuery();
  const basicData = basicDataRes?.data?.data || {};
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();

  // Use debounced search hook
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const {
    data: groupsRes,
    isLoading,
    isFetching,
  } = useGetMakkahGroupsQuery({
    query: debouncedValue,
    page: currentPage,
    ...filters,
  });

  const groups = groupsRes?.data?.data || [];
  const meta = groupsRes?.data?.meta;
  const loading = isLoading || isFetching;
  const [selectedMissionId, setSelectedMissionId] = useState(null);

  const { data: missionCompaniesRes } = useMissionCompaniesQuery(selectedMissionId);
  const missionCompanies = missionCompaniesRes?.data?.data?.companies ?? [];

  // Delete mutation
  const { mutate: deleteGroup, isPending: isDeleting } =
    useDeleteMakkahGroupMutation();

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterApply = (filterData) => {
    const prevMission = filters.mission_name;
    const nextMission = filterData.mission_name;
    const next = { ...filterData };
    if (String(prevMission ?? "") !== String(nextMission ?? "")) {
      delete next.group_name_mapping_id;
    }
    setFilters(next);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const handleFilterReset = () => {
    setFilters({});
    setCurrentPage(1);
    setSelectedMissionId(null);
  };

  useEffect(() => {
    setSelectedMissionId(filters.mission_name ?? null);
  }, [filters.mission_name]);

  // Handle details modal
  const handleShowDetails = (details) => {
    if (details && details.length > 0) {
      setDetailsModalState({ isOpen: true, details });
    }
  };

  const handleCloseDetails = () => {
    setDetailsModalState({ isOpen: false, details: [] });
  };

  // Handle delete modal
  const handleDeleteClick = (groupId) => {
    setDeleteModalState({ isOpen: true, groupId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, groupId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.groupId) {
      deleteGroup(
        { id: deleteModalState.groupId },
        {
          onSuccess: () => {
            setDeleteModalState({ isOpen: false, groupId: null });
          },
          onError: () => {
            setDeleteModalState({ isOpen: false, groupId: null });
          },
        }
      );
    }
  };

  // Helper function to lookup nationality/organizer_country codes
  const lookupCountryName = (countryCode) => {
    return <SelectCountry.Display value={countryCode} />;
  };

  // Helper function to get service center name
  const getServiceCenterName = (code) => {
    if (!code) return "-";
    const serviceCenter = (basicData?.service_centers || []).find(
      (sc) => String(sc.code) === String(code)
    );
    return serviceCenter ? serviceCenter.name : code || "-";
  };

  // Define columns configuration with render functions
  const columns = useMemo(
    () => [
      {
        name: t("GROUP_NUMBER"),
        key: "group_number",
        render: (value, rowData) =>
          canViewGroup ? (
            <Link className="fw-bold" to={`${rowData.id}/view`}>
              {getLocalizedNumber(value)}
            </Link>
          ) : (
            <span className="fw-bold">{getLocalizedNumber(value)}</span>
          ),
      },
      {
        name: t("MAKKAH_GROUPS_COL_ORGANIZATION_COUNTRY"),
        key: "organizer_country",
        render: (value) => lookupCountryName(value),
      },
      {
        name: t("MAKKAH_GROUPS_COL_ORGANIZER_NAME"),
        key: "group_name",
        render: ($, rowData) =>
          [
            rowData?.delegation_detail?.id,
            getLocalizedText(
              rowData?.delegation_detail?.nameAr,
              rowData?.delegation_detail?.nameEn
            ),
          ].join(" - "),
      },
      {
        name: t("MAKKAH_GROUPS_COL_NATIONALITY"),
        key: "nationality",
        render: (value) => lookupCountryName(value),
      },
      {
        name: t("AFFILIATED_ORGANIZER"),
        key: "affiliated_organizer_detail",
        render: (value) => getLocalizedText(value?.companyNameAr, value?.companyNameEn),
      },
      {
        name: t("GROUPS"),
        key: "group_name",
        render: ($, rowdata) => {
          const groupNames = rowdata?.details?.map(item => item.group_name)

          return (
            <CountModal
              count={groupNames.length}
              modal={{
                props: {
                  size: "lg",
                  centered: true,
                  scrollable: true,
                },
                header: {
                  children: t("GROUPS"),
                },
                body: {
                  children: (
                    <ul style={{ paddingInlineStart: "15px" }}>
                      {
                        groupNames.map(name => (
                          <li key={name}>
                            {name}
                          </li>
                        ))
                      }
                    </ul>
                  ),
                },
              }}
            >
              {groupNames?.[0]}
            </CountModal>)
        },
      },
      {
        name: t("MAKKAH_GROUPS_COL_BATCH"),
        key: "batch",
        render: (value) => value || "-",
        visible: false,
      },
      {
        name: t("ORGANIZER_NAME"),
        key: "supervisor_name",
        render: (value) => value || "-",
        visible: false,
      },
      {
        name: t("MAPPED_GROUP_NAME"),
        key: "group_name_mapping_id",
        render: (value, rowData) => rowData?.pilgrim_group_name_mapping?.mapped_group_name || "-",
      },
      {
        name: t("MOBILE_NUMBER"),
        key: "supervisor_mobile_number",
        render: (value) => (
          <span dir="ltr">{getLocalizedNumber(value) || "-"}</span>
        ),
      },
      {
        name: t("MAKKAH_GROUPS_COL_SERVICE_CENTER"),
        key: "service_center_id",
        render: (value) => getServiceCenterName(value),
        visible: false,
      },
      {
        name: t("MAKKAH_GROUPS_COL_TOTAL_PILGRIMS"),
        key: "total_pilgrims",
        filterable: false,
        render: ($, rowData) => {
          const totalPilgrims = (rowData?.details || []).reduce(
            (sum, d) => sum + (Number(d.capacity) || 0),
            0
          );
          return getLocalizedNumber(totalPilgrims) || "-";
        },
      },
      {
        name: t("MAKKAH_GROUPS_COL_DETAILS"),
        key: "details",
        filterable: false,
        render: ($, rowData) => {
          const details = rowData?.details || [];
          if (details.length === 0) {
            return <span className="text-muted">-</span>;
          }
          return (
            <button
              className="H6 fw-bold text-truncate btn btn-link p-0"
              onClick={(e) => {
                e.preventDefault();
                handleShowDetails(details);
              }}
            >
              {details[0]?.contract_number}
              {details.length > 1 && (
                <small className="ms-1">
                  +{getLocalizedNumber(details.length - 1)}
                </small>
              )}
            </button>
          );
        },
      },
      {
        name: t("MAKKAH_GROUPS_COL_ACTIONS"),
        key: "actions",
        filterable: false,
      },
    ],
    [t, getLocalizedNumber, getLocalizedText, getServiceCenterName, canViewGroup]
  );

  // Define actions function
  const getActions = (rowData) => {
    const actions = [];
    if (canViewGroup) {
      actions.push({
        label: (
          <Link
            to={`${rowData.id}/view`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={18} icon={ViewIcon} />
            <span>{t("VIEW")}</span>
          </Link>
        ),
        key: "view",
      });
    }
    if (canUpdateMainGroup) {
      actions.push({
        label: (
          <Link
            to={`/app/${currentSystem}/basic-data/groups-upcoming/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={18} icon={Edit02Icon} />
            <span>{t("EDIT_GROUP")}</span>
          </Link>
        ),
        key: "edit",
      });
    }
    if (canAccessSegments) {
      actions.push({
        label: (
          <Link
            to={`${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={18} icon={PieChartIcon} />
            <span>{t("MANAGE_SEGMENTS")}</span>
          </Link>
        ),
        key: "add-details",
      });
    }
    if (actions.length > 0 && canDeleteGroup) {
      actions.push({ type: "divider" });
    }
    if (canDeleteGroup) {
      actions.push({
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={18} icon={DeleteIcon} />
            <span>{t("DELETE")}</span>
          </div>
        ),
        key: "delete",
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      });
    }
    return actions;
  };

  const filterFields = [
    {
      name: "date",
      label: t("FLIGHT_DATE"),
      type: "date",
    },
    {
      name: "group_number",
      label: t("GROUP_NUMBER"),
      type: "input",
    },
    {
      name: "nationality",
      label: t("NATIONALITY"),
      type: "select",
      optionKey: "nationalities",
      options: (n) => ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      }),
    },
    {
      name: "organizer_country",
      label: t("ORGANIZER_COUNTRY"),
      type: "select",
      optionKey: "nationalities",
      options: (c) => ({
        value: c.id,
        label: <SelectCountry.Display value={c.id} />,
      }),
      onChange: (value) => {},
    },
    {
      name: "mission_name",
      label: t("BUILDING_ORGANIZER"),
      type: "select",
      placeholder: t("DELEGATION"),
      optionKey: "missions",
      options: (mission) => ({
        label: getLocalizedText(mission.name_ar, mission.name_en),
        value: mission.id,
      }),
      onChange: (value) => {
        setSelectedMissionId(value ?? null);
        setFilters((f) => {
          const next = { ...f };
          if (value != null && value !== "") {
            next.mission_name = value;
          } else {
            delete next.mission_name;
          }
          delete next.group_name_mapping_id;
          return next;
        });
      },
    },
    {
      name: "company_name",
      label: t("AFFILIATED_ORGANIZER"),
      type: "select",
      disabled: !selectedMissionId,
      placeholder: t("AFFILIATED_ORGANIZER"),
      options: selectedMissionId
        ? missionCompanies.map((c) => ({
          value: c.id,
          label: getLocalizedText(c.companyNameAr, c.companyNameEn) || getLocalizedText(c.name_ar, c.name_en),
        }))
        : [],
      onChange: (value) => {
        setFilters((f) => {
          const next = { ...f };
          if (value != null && value !== "") {
            next.company_name = value;
          } else {
            delete next.company_name;
          }
          delete next.group_name_mapping_id;
          return next;
        });
      },
    },
    {
      name: "service_center_id",
      label: t("SERVICE_CENTER"),
      type: "select",
      optionKey: "service_centers",
      options: (sc) => ({
        value: sc.id,
        label: sc.name,
      }),
    },
    {
      name: "group_name_mapping_id",
      label: t("MAPPED_GROUP_NAME"),
      type: "infiniteSelect",
      Component: (componentProps) => (
        <PilgrimGroupNameMappingInfiniteSelect
          {...componentProps}
          mission_id={selectedMissionId}
          company_id={filters.company_name}
        />
      ),
      placeholder: t("MAPPED_GROUP_NAME"),
    },
    {
      name: "makkah_contract_id",
      label: t("MAKKAH_CONTRACTS"),
      type: "select",
      optionKey: "contracts",
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "madinah_contract_id",
      label: t("MADINAH_CONTRACTS"),
      type: "select",
      optionKey: "contracts",
      options: (c) => ({
        value: c.id,
        label: [c.id, c.house_commercial_name_ar].join(" - "),
      }),
    },
    {
      name: "direction_id",
      label: t("DIRECTION"),
      type: "select",
      optionKey: "directions",
      options: (d) => ({
        value: d.id,
        label: d.name,
      }),
      filter: (d) => d.id === 1 || d.id === 2,
    },
  ];

  // Add button dropdown
  const addButton = canCreateGroup ? (
    <Dropdown
      trigger={["click"]}
      disabled={loading}
      menu={{
        items: [
          {
            label: (
              <Link to={`/app/${currentSystem}/basic-data/groups/create`}>
                {t("ADD_MANUAL")}
              </Link >
            ),
            icon: <HugeiconsIcon icon={AddSquareIcon} />,
            key: "add",
          },
          {
            label: t("ADD_FROM_EXCEL"),
            key: "add-from-excel",
            icon: <FileExcelIcon />,
            onClick: () => setShowImportModal(true),
          },
        ],
      }}
    >
      <span>
        <BtnAsLink
          className="btn-primary"
          icon={<HugeiconsIcon icon={AddSquareIcon} />}
          disabled={loading}
        >
          {t("ADD")}
        </BtnAsLink>
      </span>
    </Dropdown >
  ) : null;

  return (
    <Container fluid>
      <TableC
        data={groups}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("MAKKAH_GROUPS_SEARCH_PLACEHOLDER")}
        pagination={{
          current: currentPage || 1,
          total: meta?.total ?? 0,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
      }}
        actions={getActions}
        filterFields={filterFields}
        filters={filters}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        emptyStateTitle={
          debouncedValue
            ? t("MAKKAH_GROUPS_NO_RESULTS")
            : t("MAKKAH_GROUPS_NO_GROUPS")
        }
        emptyStateDescription={
          debouncedValue
            ? t("MAKKAH_GROUPS_NO_RESULTS_DESCRIPTION")
            : t("MAKKAH_GROUPS_NO_GROUPS_DESCRIPTION")
        }
        headerKey="group_name"
        minVisibleColumns={2}
        skeletonCount={3}
        renderActions={addButton}
      />

      {/* Details Modal */}
      <DisplayDetailsModal
        details={detailsModalState.details}
        open={detailsModalState.isOpen}
        onClose={handleCloseDetails}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_GROUP_ITEM"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />

      {/* Import Campaign Modal */}
      <ImportCampaignModal
        isOpen={showImportModal}
        toggle={() => setShowImportModal(false)}
      />
    </Container>
  );
};

export default UpcommingGroups;
